import { useQuery, useMutation } from '@apollo/react-hooks';

import { UpdateContentPermissionsMutation } from '../graphql/UpdateContentPermissionsMutation.graphql';
import { ContentPermissionsQuery } from '../graphql/ContentPermissionsQuery.graphql';

type ContentPermissionsQueryProps = {
	contentId: string;
	mentionedUserId: string | null;
	currentUserId: string | null;
	isMentionsNotificationsEnabled: boolean;
};

type User = {
	node: {
		id: string;
	};
};

type ContentPermissionsData = {
	contentPermissions: {
		subjectsWithEffectiveContentPermissions: {
			edges: User[];
		};
		contentAccess: string;
	};
	userWithContentRestrictions: {
		restrictingContent: {
			hasRestrictions: boolean;
		};
		hasSpaceViewPermission: boolean;
	};
};

export const useContentPermissions = ({
	contentId,
	mentionedUserId,
	currentUserId,
	isMentionsNotificationsEnabled,
}: ContentPermissionsQueryProps) => {
	const {
		data: contentPermissionsData,
		error: contentPermissionsError,
		loading: contentPermissionsLoading,
	} = useQuery<ContentPermissionsData>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		ContentPermissionsQuery,
		{
			variables: { contentId, accountId: mentionedUserId },
			skip:
				!isMentionsNotificationsEnabled || !mentionedUserId || currentUserId === mentionedUserId,
			fetchPolicy: 'cache-and-network',
		},
	);

	const [updateContentPermissionMutation] = useMutation(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		UpdateContentPermissionsMutation,
	);

	let userHasAccessToContent: Boolean | null = null;
	let isParentPageRestricted = false;
	let isSpaceRestricted = false;

	// `contentPermissions` specifies restrictions on the current page
	// `userWithContentRestrictions` specifies restrictions on the current page's parent, if one exists
	if (contentPermissionsData && !contentPermissionsLoading) {
		const { contentPermissions, userWithContentRestrictions } = contentPermissionsData;
		if (!userWithContentRestrictions.hasSpaceViewPermission) {
			userHasAccessToContent = false;
			isSpaceRestricted = true;
		} else if (userWithContentRestrictions.restrictingContent?.hasRestrictions) {
			// Parent page has content restrictions, which is inherited by this child page
			userHasAccessToContent = false;
			isParentPageRestricted = true;
		} else {
			// Not a parent page, check permissions on this page
			const usersWithAccess = contentPermissions?.subjectsWithEffectiveContentPermissions?.edges;

			userHasAccessToContent =
				usersWithAccess?.some((user: User) => user.node.id === mentionedUserId) ||
				contentPermissions?.contentAccess !== 'EVERYONE_NO_ACCESS';
		}
	}

	return {
		userHasAccessToContent,
		isParentPageRestricted,
		isSpaceRestricted,
		contentPermissionsError,
		contentPermissionsLoading,
		updateContentPermissionMutation,
	};
};
